import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const numberFmt = (n, digits = 2) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

const intFmt = (n) => new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(Number.isFinite(+n) ? +n : 0);

const dateToInputValue = (d) => new Date(d).toISOString().slice(0, 16);
const clean = (v) => (v && typeof v === 'object' ? 0 : v ?? 0);

const COLUMNS = [
  { key: 'Product / Item Name', label: 'Product / Item Name' },
  { key: 'Qty Sold', label: 'Qty Sold', align: 'right' },
  { key: 'Unit Price', label: 'Unit Price', align: 'right' },
  { key: 'Total Amount', label: 'Total Amount', align: 'right' },
  { key: 'Sales Type', label: 'Sales Type' },
  { key: 'Remarks', label: 'Remarks' },
];

const ItemSaleReport = ({ Currency }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 0,
  });

  const now = new Date();
  const tenDaysAgo = new Date(now);
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: dateToInputValue(tenDaysAgo),
    toDate: dateToInputValue(now),
  });

  const [salesTypeFilter, setSalesTypeFilter] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [sortState, setSortState] = useState({ key: 'Total Amount', dir: 'desc' });

  const pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ];

  // ⭐ Fetch Branch List
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');
      const list = res?.data?.result || [];

      setBranches(
        list.map((b) => ({
          value: b.id,
          label: b.branchName,
        }))
      );
    } catch (error) {
      console.error('Branch Load Error: ', error);
    }
  };

  // ⭐ Fetch Data — updated with BranchId = 0 fix
  const fetchData = async (page = pagination.currentPage, size = pagination.pageSize) => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      params.append('PageNo', page);
      params.append('PageSize', size);
      params.append('ReportType', 'Product Sales Detail');
      params.append('Measure', 'Revenue');
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);
      params.append('Comparison', 'No Comparison');

      // ⭐ FIX → Always send BranchId (0 = all branches)
      params.append('BranchId', selectedBranch?.value || 0);

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];

      setRows(result);
      setPagination((p) => ({
        ...p,
        ...(res.data.paginationData || {}),
        pageSize: size,
      }));
    } catch (error) {
      console.error(error);
      setErr('Failed to fetch report. Please try again.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived Filters
  const salesTypeOptions = useMemo(() => {
    const types = new Set();
    rows.forEach((r) => r['Sales Type'] && types.add(String(r['Sales Type'])));
    return Array.from(types)
      .sort()
      .map((s) => ({ value: s, label: s }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let r = rows;
    if (salesTypeFilter?.value) r = r.filter((x) => String(x['Sales Type']) === salesTypeFilter.value);

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      r = r.filter((x) =>
        String(x['Product / Item Name'] || '')
          .toLowerCase()
          .includes(q)
      );
    }
    return r;
  }, [rows, salesTypeFilter, searchText]);

  const sortedRows = useMemo(() => {
    const { key, dir } = sortState || {};
    const copy = [...filteredRows];

    copy.sort((a, b) => {
      const av = clean(a[key]);
      const bv = clean(b[key]);

      const na = Number(av),
        nb = Number(bv);
      if (Number.isFinite(na) && Number.isFinite(nb)) return dir === 'asc' ? na - nb : nb - na;

      return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

    return copy;
  }, [filteredRows, sortState]);

  const totals = useMemo(() => {
    const qty = sortedRows.reduce((s, r) => s + (+clean(r['Qty Sold']) || 0), 0);
    const amount = sortedRows.reduce((s, r) => s + (+clean(r['Total Amount']) || 0), 0);
    const avgUnit = qty ? amount / qty : 0;

    return { qty, amount, avgUnit };
  }, [sortedRows]);

  // Handlers
  const onChangeRange = (e) => setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSearch = () => fetchData(1, pagination.pageSize);
  const onPageSize = (opt) => {
    const size = opt?.value || 20;
    setPagination((p) => ({ ...p, pageSize: size }));
    fetchData(1, size);
  };

  const gotoPage = (next) => {
    if (!next || next === pagination.currentPage) return;
    fetchData(next, pagination.pageSize);
  };

  const onSort = (key) => setSortState((prev) => (!prev || prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));

  const exportCSV = () => {
    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = sortedRows.map((r) => COLUMNS.map((c) => `"${String(clean(r[c.key])).replace(/"/g, '""')}"`).join(',')).join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `product-sales-detail-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-5">
      <button className="btn addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* ---------------------------------------------------------------- */}
      {/* Filters */}
      {/* ---------------------------------------------------------------- */}
      <div className="row g-3 align-items-end">
        {/* ⭐ Branch Dropdown */}
        <div className="col-12 col-md-3">
          <label className="form-label">Select Branch</label>
          <Select
            value={selectedBranch}
            onChange={setSelectedBranch}
            options={branches}
            placeholder="Select Branch"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            isClearable
          />
        </div>

        <div className="col-12 col-md-3">
          <label className="form-label">
            {t('from')} {t('date')}
          </label>
          <input type="date" className="form-control" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
        </div>

        <div className="col-12 col-md-3">
          <label className="form-label">
            {t('to')} {t('date')}
          </label>
          <input type="date" className="form-control" name="toDate" value={formData.toDate} onChange={onChangeRange} />
        </div>

        <div className="col-12 col-md-3">
          <label className="form-label">Search Item</label>
          <input type="text" className="form-control" placeholder="Type to filter…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        </div>

        <div className="col d-flex gap-2 mt-4 mb-4">
          <button className="addItem-btn col-2" onClick={onSearch} disabled={loading}>
            {loading ? t('loading') || 'Loading…' : t('Search')}
          </button>

          <Select
            className="ms-auto"
            value={pageSizeOptions.find((o) => o.value === pagination.pageSize)}
            onChange={onPageSize}
            options={pageSizeOptions}
            isSearchable={false}
          />

          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!sortedRows.length}>
            Export CSV
          </button>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Table */}
      {/* ---------------------------------------------------------------- */}
      <div className="POsgridshad rounded p-0 mt-4">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead style={{ position: 'sticky', top: 0, zIndex: 0 }}>
              <tr>
                {COLUMNS.map((c) => {
                  const sorted = sortState?.key === c.key;
                  return (
                    <th
                      key={c.key}
                      onClick={() => onSort(c.key)}
                      className={`${c.align === 'right' ? 'text-end' : ''} ${sorted ? 'table-active' : ''}`}
                      style={{ cursor: 'pointer' }}
                    >
                      {c.label}
                      {sorted ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {!loading && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center text-muted py-4">
                    No data found
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center py-4">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading &&
                sortedRows.map((r, i) => (
                  <tr key={`${r['Product / Item Name']}-${i}`}>
                    <td>{r['Product / Item Name'] ?? '—'}</td>
                    <td className="text-end">{intFmt(clean(r['Qty Sold']))}</td>
                    <td className="text-end">
                      {Currency?.currency?.code} {numberFmt(clean(r['Unit Price']))}
                    </td>
                    <td className="text-end fw-semibold">
                      {Currency?.currency?.code} {numberFmt(clean(r['Total Amount']))}
                    </td>
                    <td>{r['Sales Type'] ?? '—'}</td>
                    <td>{r['Remarks'] || '—'}</td>
                  </tr>
                ))}
            </tbody>

            {sortedRows.length > 0 && (
              <tfoot>
                <tr className="bg-light fw-bold">
                  <td>Total</td>
                  <td className="text-end">{intFmt(totals.qty)}</td>
                  <td className="text-end">—</td>
                  <td className="text-end">
                    {Currency?.currency?.code} {numberFmt(totals.amount)}
                  </td>
                  <td colSpan={2}>
                    Avg Unit: {Currency?.currency?.code} {numberFmt(totals.avgUnit)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        {(pagination.totalRecords ?? 0) > pagination.pageSize && (
          <div className="d-flex justify-content-between align-items-center p-3">
            <div className="text-muted small">Total • {pagination.totalRecords} records</div>

            {/* <div className="btn-group">
              <button
                className="btn btn-outline-secondary"
                disabled={loading || pagination.currentPage <= 1}
                onClick={() => gotoPage(pagination.currentPage - 1)}
              >
                Prev
              </button>

              <button
                className="btn btn-outline-secondary"
                disabled={loading || pagination.currentPage >= pagination.totalPages}
                onClick={() => gotoPage(pagination.currentPage + 1)}
              >
                Next
              </button>
            </div> */}
          </div>
        )}
      </div>
    </div>
  );
};

const mapStatetoProps = (state) => state;
export default connect(mapStatetoProps)(ItemSaleReport);
